/**
 * 관세청 변경 내역(시계열) — 기준일 이후 "적용개시(strt)"된 세번 리스트.
 * 게시판처럼 날짜로 조회. 행 클릭 → 해당 호 상세로 이동.
 * 출처: customs_hs_master.rates_full[*].strt (UNI-PASS 009Q 적용기간).
 */
import React, { useEffect, useState } from "react";

function fmtHsk(s) {
  s = String(s || "");
  if (s.length === 10) return `${s.slice(0, 4)}.${s.slice(4, 6)}.${s.slice(6)}`;
  if (s.length === 6) return `${s.slice(0, 4)}.${s.slice(4)}`;
  if (s.length === 4) return `${s.slice(0, 2)}.${s.slice(2)}`;
  return s;
}
function fmtDate(s) {
  s = String(s || "");
  return s.length === 8 ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6)}` : s;
}
const toYmd = (iso) => String(iso || "").replace(/-/g, "");
// name_ko 의 "/양허유형;…" 오염 제거
function cleanName(s) {
  return String(s || "")
    .split("/양허유형")[0]
    .trim();
}
// 세율코드 → 한글 구분(자주 쓰는 것만; 그 외 코드 원문 표기)
const RATE_LABEL = {
  A: "기본관세",
  C: "WTO협정",
  P: "할당관세",
  W: "WTO할당",
  Z: "조정관세",
  R: "최빈국특혜",
  FEU1: "EU(FTA)",
  FGB1: "영국(FTA)",
  FCN1: "중국(FTA)",
  FUS1: "미국(FTA)",
  FAS1: "아세안(FTA)",
  FVN1: "베트남(FTA)",
  FAU1: "호주(FTA)",
  FRCAS1: "RCEP아세안",
  FRCCN1: "RCEP중국",
  FRCJP1: "RCEP일본",
};
function rateLabel(code) {
  return RATE_LABEL[code] || code;
}
// 기준일 이후 적용개시되는 세율들 → "구분 값% (적용 개시~종료)" 요약
function changeSummary(changes, sinceYmd) {
  const arr = (changes || []).filter((c) => c && c.strt && c.strt >= sinceYmd);
  if (!arr.length) return null;
  const top = arr[0];
  const val = top.value != null && top.value !== "" ? `${top.value}%` : "";
  const period = `적용 ${fmtDate(top.strt)}${top.end ? `~${fmtDate(top.end)}` : "~"}`;
  const more = arr.length > 1 ? ` 외 ${arr.length - 1}건` : "";
  return `${rateLabel(top.code)} ${val} · ${period}${more}`.trim();
}

export default function HsChangesView({ onOpen }) {
  const [since, setSince] = useState("2026-06-01");
  const [items, setItems] = useState(null);

  const load = (sinceIso) => {
    setItems(null);
    fetch(`/api/onto/hs-changes?since=${toYmd(sinceIso)}&limit=300`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setItems((d && d.items) || []))
      .catch(() => setItems([]));
  };
  useEffect(() => {
    load(since);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <div style={bar}>
        <span style={{ fontSize: 12.5, color: "var(--audit-text-muted)" }}>
          기준일 이후 적용개시
        </span>
        <input
          type="date"
          value={since}
          onChange={(e) => {
            setSince(e.target.value);
            load(e.target.value);
          }}
          style={dateInput}
        />
        <span style={{ fontSize: 11.5, color: "var(--audit-text-faint)" }}>
          {items ? `${items.length}건` : ""}
        </span>
      </div>

      {items === null ? (
        <div style={empty}>불러오는 중…</div>
      ) : !items.length ? (
        <div style={empty}>
          이 날짜 이후 적용개시(변경)된 세번이 없습니다.
          <br />
          관세청 데이터(적용개시일)가 갱신되면 여기에 시계열로 표시됩니다.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {items.map((it) => (
            <div
              key={it.hsk_code}
              onClick={() => onOpen?.(it.hsk_code)}
              style={row}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "var(--audit-bg-hover)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "var(--audit-bg-elevated)")
              }
            >
              <span style={dateCell}>{fmtDate(it.strt)}</span>
              <span style={codeCell}>{fmtHsk(it.hsk_code)}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={nameCell}>{cleanName(it.name)}</span>
                {changeSummary(it.changes, toYmd(since)) && (
                  <span style={chgCell}>
                    {changeSummary(it.changes, toYmd(since))}
                  </span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const bar = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  marginBottom: 12,
  flexWrap: "wrap",
};
const dateInput = {
  fontSize: 12.5,
  padding: "6px 9px",
  borderRadius: 7,
  border: "1px solid var(--audit-border)",
  background: "var(--audit-bg)",
  color: "var(--audit-text)",
  fontFamily: "inherit",
};
const empty = {
  padding: "22px 16px",
  color: "var(--audit-text-muted)",
  fontSize: 13,
  lineHeight: 1.6,
  border: "1px dashed var(--audit-border)",
  borderRadius: 8,
  background: "var(--audit-bg-subtle)",
};
const row = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "10px 13px",
  border: "1px solid var(--audit-border)",
  borderRadius: 8,
  background: "var(--audit-bg-elevated)",
  cursor: "pointer",
};
const dateCell = {
  flexShrink: 0,
  fontFamily: "JetBrains Mono, monospace",
  fontSize: 11.5,
  fontWeight: 700,
  color: "var(--audit-accent-dark)",
  width: 90,
};
const codeCell = {
  flexShrink: 0,
  fontFamily: "JetBrains Mono, monospace",
  fontSize: 12.5,
  color: "var(--audit-text)",
  width: 110,
};
const nameCell = {
  display: "block",
  fontSize: 13,
  color: "var(--audit-text)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const chgCell = {
  display: "block",
  marginTop: 3,
  fontSize: 11.5,
  fontWeight: 600,
  color: "var(--audit-accent-dark)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
